import QRCode from 'react-qr-code'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import ManagePassSubpageLayout from '/components/passes/manage-pass-subpage-layout'
import PassEmailCopyActions from '/components/pass-email-copy-actions'
import { AppContext } from '/providers/app-context'
import HelpText from '/help/pass-installer'

import { getPass } from '/providers/passes-api'
import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'

const PassInstaller = () => {
  const navigate = useNavigate()
  const { templates, apiSource, apiKey, passes, setPasses } = useContext(AppContext)
  const { templateId, passId } = useParams()
  const location = useLocation()
  const [pass, setPass] = useState(() => location.state?.pass ?? passes.find(item => item.template_id === templateId && item.pass_id === passId))
  const template = useMemo(() => templates.find(item => item.template_id === templateId), [templates, templateId])

  const getPassData = useCallback(async () => {
    const result = await getPass({ apiSource, apiKey, passId })
    if (result.ok === false || !result.pass) return navigate('/templates')

    setPasses(current => {
      const index = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
      if (index < 0) return [...current, result.pass]
      const next = [...current]
      next[index] = result.pass
      return next
    })
    setPass(result.pass)
  }, [apiKey, apiSource, navigate, passId, setPasses, templateId])

  useEffect(() => {
    if (!pass) getPassData()
  }, [getPassData, pass])

  return (
    <ManagePassSubpageLayout
      title={`Install Pass (${pass?.pass_id})`}
      HelpText={HelpText}
      heading={`${pass?.pass_id} ${pass?.mode === 'test' ? ' [test]' : ''}`}
      template={template}
      pass={pass}
      onClose={() => navigate(`/templates/${templateId}/passes`)}
    >
      <div className='overflow-auto h-full pb-10'>
        <Content pass={pass} />
      </div>
    </ManagePassSubpageLayout>
  )
}

export default PassInstaller

const Content = ({ pass }) => {
  if (!pass) return null

  const installUrl = pass.short_url ?? pass.shortUrl ?? (pass.public_id ? `https://api.smartix.uk/i/${pass.public_id}` : null)

  return (
    <div className='flex flex-row gap-40 items-center m-10 justify-around'>
      <div className='flex flex-col'>
        <div className='flex flex-row gap-20 p-5'>
          <div className='flex flex-col gap-10 items-start'>
            <div style={{ height: 100 }} className='flex flex-col justify-around'>
              <Link to={pass.apple_url} target='_pass'><img src={appleButton} width={250} /></Link>
            </div>
            <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
              <QRCode size={256} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} value={pass.apple_url} viewBox='0 0 256 256' />
            </div>
            <CopyToClipboard text={pass.apple_url} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
              <Button><ContentCopyIcon color='inherit' className='mr-5' />Copy URL to clipboard</Button>
            </CopyToClipboard>
          </div>

          <div className='flex flex-col gap-10 items-start'>
            <div style={{ height: 100 }} className='flex flex-col justify-around'>
              <Link to={pass.google_url} target='_pass'><img src={googleButton} width={250} /></Link>
            </div>
            <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
              <QRCode size={256} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} value={pass.google_url} viewBox='0 0 256 256' />
            </div>
            <CopyToClipboard text={pass.google_url} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
              <Button><ContentCopyIcon color='inherit' className='mr-5' />Copy URL to clipboard</Button>
            </CopyToClipboard>
          </div>
        </div>
      </div>

      <div className='flex flex-col'>
        <div className='flex flex-col gap-5 p-5'>
          <Typography variant='h6'>Pass ID:
            <CopyToClipboard text={pass.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })} options={{ format: 'text/plain' }}>
              <Button><ContentCopyIcon color='inherit' className='mr-5' />{pass.pass_id}</Button>
            </CopyToClipboard>
          </Typography>

          <Typography variant='h6'>Install link:
            {installUrl
              ? <CopyToClipboard text={installUrl} onCopy={() => enqueueSnackbar({ message: 'Install link copied', variant: 'success' })} options={{ format: 'text/plain' }}>
                <Button><ContentCopyIcon color='inherit' className='mr-5' />{installUrl}</Button>
              </CopyToClipboard>
              : <span className='ml-5'>Unavailable</span>}
          </Typography>

          <PassEmailCopyActions appleUrl={pass.apple_url} googleUrl={pass.google_url} />
        </div>
      </div>
    </div>
  )
}
